// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useEffect, useState } from 'react'
import { useLocales } from 'react-localized'
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquarePlus } from '@fortawesome/free-solid-svg-icons'
import { IonButton, IonCol, IonGrid, IonRow, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react'
import { SectionContainer, SectionPage, Table, useSession } from '../lib'
import ProcurementSectionMenu from './ProcurementSectionMenu'
import { ProcurementClient } from '../api'
import SectionLoading from '../lib/webid/SectionLoading'
import ProcurementOrder from '../api/PurchaseOrder'


const PurchaseOrderDashboardToolbar: React.FC = () => {
  const history = useHistory()
  const {user} = useSession()
  const {gettext} = useLocales()
  return (
    <IonButton
      disabled={!user.isAuthenticated()}
      fill="clear"
      onClick={() => history.push('/procurement/orders/create')}
    >
      <FontAwesomeIcon icon={faSquarePlus}/>
      {gettext("Place order")}
    </IonButton>
  )
}


const PurchaseOrderList: React.FC = () => {
  const client = new ProcurementClient()
  const [version, setVersion] = useState<Date>(null!)
  const [orders, setOrders] = useState<any[] | null>(null)
  const {gettext} = useLocales()
  const session = useSession()
  const isAuthenticated = session.isAuthenticated()

  useEffect(() => {
    if (isAuthenticated) {
      client.listOrders()
        .then(setOrders)
        .catch(() => session.setError(true))
    }
  }, [version, isAuthenticated])

  useIonViewWillLeave(() => setOrders(null))
  useIonViewWillEnter(() => setVersion(new Date()))

  return (
    <SectionPage>
      <SectionContainer
        menuComponent={ProcurementSectionMenu}
        title={gettext("Orders")}
        ready={true}
        toolbarComponent={PurchaseOrderDashboardToolbar}
      >
        {(!orders) ? (<SectionLoading/>) : (
          <IonGrid className="pt-0" style={{display: "flex", flexDirection: "column", overflowY: "scroll"}}>
            <IonRow>
              <IonCol
                size="12"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "0px",
                  maxHeight: "var(--section-body-height)",
                  overflowY: "hidden"
                }}
              >
                <Table
                  columns={[
                    {
                      key: "ref",
                      label: gettext("Reference"),
                      width: 30,
                      render: (row: ProcurementOrder, index: number) => {
                        return row.getReference()
                      }
                    },
                    {
                      key: "invoice_ref",
                      label: gettext("Invoice #"),
                      width: 30,
                      render: (row: ProcurementOrder, index: number) => {
                        return row.getInvoiceReference()
                      }
                    },
                    {
                      key: "placed",
                      label: gettext("Placed"),
                      width: 60,
                      render: (row: ProcurementOrder, index: number) => {
                        return (new Date(Date.parse(row.getPlaced()))).toLocaleDateString()
                      }
                    },
                    {
                      key: "expected_delivery",
                      label: gettext("Expected"),
                      width: 30,
                      render: (row: ProcurementOrder, index: number) => {
                        return (new Date(Date.parse(row.getExpectedDelivery()))).toLocaleDateString()
                      }
                    },
                    {
                      key: "purchaser",
                      label: gettext("Purchaser"),
                      width: 30,
                      render: (row: ProcurementOrder, index: number) => {
                        return row.getPurchaser()
                      }
                    },
                    {
                      key: "buyer",
                      label: gettext("Buyer"),
                      width: 30,
                      render: (row: ProcurementOrder, index: number) => {
                        return row.getBuyerName()
                      }
                    },
                    {
                      key: "supplier",
                      label: gettext("Supplier"),
                      width: 30,
                      render: (row: ProcurementOrder, index: number) => {
                        return row.getSupplierName()
                      }
                    },
                    {
                      key: "view",
                      label: gettext("View"),
                      width: 30,
                      render: (row: ProcurementOrder, index: number) => {
                        const url = `${window.location.origin.replace("backoffice", "suppliers")}/orders/${row.getId()}?code=${row.getAccessCode()}`
                        return (<a href={url} target='_blank'>{gettext("view")}</a>)
                      }
                    },
                  ]}
                  items={orders || []}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        )}
      </SectionContainer>
    </SectionPage>
  )
}

export { PurchaseOrderList }
